/**
 * Client-side weather service with stale-while-revalidate caching.
 *
 * - Always returns cached data immediately (if available)
 * - Fires background refresh when data is past its refreshAfter window
 * - Never evicts cached data — TTL is a freshness hint, not an eviction trigger
 * - First fetch blocks and returns result (or null if offline)
 */

import { db } from '../../database/schema.js'
import { weatherCacheKey, quantizeCoordinate } from './weather-utils.js'
import type {
  WeatherData,
  WeatherCacheEntry,
  WeatherResult,
  EnsembleData,
} from './weather-types.js'
import { fetchEnsemble } from './weather-ensemble.js'

/** How long before cached data should attempt a refresh (1 hour) */
const REFRESH_INTERVAL_MS = 60 * 60 * 1000

export interface GetWeatherOptions {
  dateRange?: { startDate: string; endDate: string } | undefined
  forceRefresh?: boolean | undefined
  /** Fetch ensemble spread data (expensive — only use for area forecast) */
  includeEnsemble?: boolean | undefined
  /** Use exact coordinates instead of quantizing (for waypoint-specific forecasts) */
  exactCoords?: boolean | undefined
}

export class WeatherService {
  async getWeather(
    lat: number,
    lon: number,
    dateRangeOrOptions?: { startDate: string; endDate: string } | GetWeatherOptions,
    forceRefresh = false
  ): Promise<WeatherResult | null> {
    // Support both old signature (dateRange, forceRefresh) and new options object
    let opts: GetWeatherOptions
    if (
      dateRangeOrOptions &&
      'startDate' in dateRangeOrOptions &&
      'endDate' in dateRangeOrOptions
    ) {
      opts = { dateRange: dateRangeOrOptions, forceRefresh }
    } else {
      opts = (dateRangeOrOptions as GetWeatherOptions) ?? {}
    }

    const { dateRange, includeEnsemble = false, exactCoords = false } = opts
    const shouldForceRefresh = opts.forceRefresh ?? forceRefresh

    const apiLat = exactCoords ? lat : quantizeCoordinate(lat)
    const apiLon = exactCoords ? lon : quantizeCoordinate(lon)

    const rangeSuffix = dateRange ? `:${dateRange.startDate}:${dateRange.endDate}` : ''
    const cacheId =
      (exactCoords ? `weather:v3:${lat}:${lon}` : weatherCacheKey(lat, lon)) + rangeSuffix

    const cached = !shouldForceRefresh ? await db.weatherCache.get(cacheId) : undefined

    if (cached) {
      const now = new Date()
      const refreshAfter = new Date(cached.refreshAfter)
      const isFresh = now < refreshAfter

      if (!isFresh) {
        void this.refreshInBackground(apiLat, apiLon, cacheId, dateRange, includeEnsemble)
      }

      return {
        data: cached.data,
        fresh: isFresh,
        fetchedAt: cached.fetchedAt,
        ensemble: cached.ensemble ?? null,
      }
    }

    // No cached data — must fetch (blocking)
    const fetches: [Promise<WeatherData | null>, Promise<EnsembleData | null>] = [
      this.fetchFromApi(apiLat, apiLon, dateRange),
      includeEnsemble
        ? fetchEnsemble(apiLat, apiLon, {
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate,
          }).catch(() => null)
        : Promise.resolve(null),
    ]
    const [data, ensemble] = await Promise.all(fetches)
    if (!data) {
      return null
    }

    await this.writeCache(cacheId, apiLat, apiLon, data, ensemble)

    return {
      data,
      fresh: true,
      fetchedAt: new Date().toISOString(),
      ensemble,
    }
  }

  async prefetch(lat: number, lon: number): Promise<void> {
    const cacheId = weatherCacheKey(lat, lon)
    const cached = await db.weatherCache.get(cacheId)

    if (cached) {
      const now = new Date()
      const refreshAfter = new Date(cached.refreshAfter)
      if (now < refreshAfter) {
        return
      }
    }

    const qLat = quantizeCoordinate(lat)
    const qLon = quantizeCoordinate(lon)
    const data = await this.fetchFromApi(qLat, qLon)
    if (data) {
      await this.writeCache(cacheId, qLat, qLon, data)
    }
  }

  private async refreshInBackground(
    lat: number,
    lon: number,
    cacheId: string,
    dateRange?: { startDate: string; endDate: string },
    includeEnsemble = false
  ): Promise<void> {
    try {
      const [data, ensemble] = await Promise.all([
        this.fetchFromApi(lat, lon, dateRange),
        includeEnsemble
          ? fetchEnsemble(lat, lon, {
              startDate: dateRange?.startDate,
              endDate: dateRange?.endDate,
            }).catch(() => null)
          : Promise.resolve(null),
      ])
      if (data) {
        await this.writeCache(cacheId, lat, lon, data, ensemble)
      }
    } catch {
      // Silently fail — stale data remains available
    }
  }

  private async fetchFromApi(
    lat: number,
    lon: number,
    dateRange?: { startDate: string; endDate: string }
  ): Promise<WeatherData | null> {
    const base = import.meta.env.VITE_API_BASE_URL ?? ''
    let url = `${base}/weather/summary?latitude=${lat.toFixed(2)}&longitude=${lon.toFixed(2)}`
    if (dateRange) {
      url += `&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`
    }
    try {
      const response = await fetch(url)
      if (!response.ok) {
        return null
      }
      return (await response.json()) as WeatherData
    } catch {
      return null
    }
  }

  private async writeCache(
    cacheId: string,
    lat: number,
    lon: number,
    data: WeatherData,
    ensemble?: EnsembleData | null
  ): Promise<void> {
    const now = new Date()
    const entry: WeatherCacheEntry = {
      id: cacheId,
      lat,
      lon,
      data,
      ensemble: ensemble ?? null,
      fetchedAt: now.toISOString(),
      refreshAfter: new Date(now.getTime() + REFRESH_INTERVAL_MS).toISOString(),
    }
    await db.weatherCache.put(entry)
  }
}
